import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ComponentPropsWithRef,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronIcon,
  ChevronLeftIcon,
  CloseIcon,
  ErrorIcon,
  InfoIcon,
  SuccessIcon,
  WarningIcon,
} from './icons';

/**
 * The handful of primitives the project screens share. Kept in one file rather
 * than a directory of one-component modules: they exist so the long class
 * strings are written once, not to be a component library.
 *
 * A few of them are exported as bare class strings rather than components --
 * `FOCUS_RING`, `SEGMENTED_TRACK`, `segmentedItem`. That is the same idea taken
 * one step further: a segmented control is worn by a `<NavLink>` in one place, a
 * `<button>` in two others, and wrapping all three in a component would mean
 * inventing a polymorphic `as` prop to hand back the elements they already had.
 */

/**
 * One focus treatment, everywhere.
 *
 * There used to be three: fields killed the outline and substituted a honey
 * ring, buttons had nothing at all, and the couple of dozen hand-rolled icon
 * buttons fell through to the browser default -- which on a dark primary button
 * is invisible. A keyboard could reach every control in the app and show you
 * where it was on roughly half of them.
 *
 * `focus-visible` rather than `focus`, so a mouse click on a button does not
 * leave a ring sitting behind it. The offset needs a colour to punch out of;
 * white covers cards, panels and rows, which is every surface a control sits on
 * except the page ground itself -- and nothing focusable sits directly on that.
 */
export const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-honey-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white';

/** For controls painted onto the tinted panel header or the property sheet,
 *  where a white offset ring would cut a hole in the tint. */
export const FOCUS_RING_FLUSH =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-honey-500';

/**
 * The shape every tick box in the app shares.
 *
 * A class string and not a component, deliberately. The three callers -- row
 * selection, mark-done in a focus session, and a checklist item -- disagree on
 * their accent, on what disabled should look like, and on which glyph still
 * reads at 16px, which is every prop a `<Checkbox>` would have had. The
 * abstraction would be larger than the duplication it removed, and the fourth
 * caller would add a fourth prop.
 *
 * What they must not disagree on is the geometry: a 16px painted box with a 28px
 * hit area, grown by a pseudo-element rather than by padding, because padding
 * moves the border out with it and draws a 28px circle instead.
 *
 * `z-10` is left to the caller -- only the two that sit inside a row with a
 * full-bleed `<Link>` overlay need to win the click against it.
 */
export const CHECKBOX_BOX =
  `relative flex h-4 w-4 items-center justify-center rounded-full border transition after:absolute after:-inset-1.5 after:content-[''] ${FOCUS_RING_FLUSH}`;

const BUTTON_BASE =
  `inline-flex items-center justify-center rounded-lg font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${FOCUS_RING}`;

/** Split out of the skin for the same reason FIELD_PADDING is: a caller cannot
 *  reliably shrink a button by appending `py-1`, because Tailwind breaks
 *  specificity ties by its own emit order rather than by source order.
 *  `compact` matches the density of the task panel, which is a property sheet
 *  rather than a page and would otherwise be mostly button. */
const BUTTON_SIZING = {
  default: 'gap-2 px-3 py-1.5 text-sm',
  compact: 'gap-1.5 px-2.5 py-1 text-dense',
} as const;

/**
 * `primary` is honey rather than near-black, and that one line is most of what
 * gives the app an identity: the accent used to appear in the logo and nowhere
 * a person actually clicks.
 *
 * It is honey-700 and not the brighter 500 or 600 because this carries white
 * text: 700 (#b45309) is 5.2:1 and passes AA, where 600 is 3.3:1 and 500 is
 * 2.1:1. Hover *darkens* to 800 for the same reason -- lightening on hover
 * would take the contrast down exactly when the pointer says the control is
 * about to be used. 500 and 600 are for fills, dots and rails, never for a
 * background behind type.
 *
 * If it ever reads too loud in situ, `bg-stone-900 hover:bg-stone-800` is the
 * fallback and this is the only line that has to change.
 */
const BUTTON_VARIANTS = {
  primary: 'bg-honey-700 text-white shadow-sm hover:bg-honey-800',
  secondary: 'border border-stone-300 bg-white text-stone-700 hover:border-stone-400 hover:bg-stone-50',
  ghost: 'text-stone-700 hover:bg-stone-100',
  danger: 'border border-red-200 bg-white text-red-700 hover:border-red-300 hover:bg-red-50',
} as const;

type ButtonVariant = keyof typeof BUTTON_VARIANTS;

const buttonClasses = (variant: ButtonVariant, compact: boolean | undefined, className: string) =>
  `${BUTTON_BASE} ${BUTTON_SIZING[compact ? 'compact' : 'default']} ${BUTTON_VARIANTS[variant]} ${className}`;

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  compact?: boolean;
  /** Swaps the leading content for a spinner and marks the control busy. The
   *  label stays put, so the button does not change width mid-save. */
  loading?: boolean;
};

export function Button({
  variant = 'secondary',
  compact = false,
  loading = false,
  className = '',
  disabled,
  children,
  ...props
}: ButtonProps) {
  return (
    <button
      type="button"
      {...props}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={buttonClasses(variant, compact, className)}
    >
      {loading ? <Spinner className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} /> : null}
      {children}
    </button>
  );
}

/**
 * A link that looks like a button.
 *
 * Projects and Assignees each had their own copy of `Button`'s class string
 * pasted onto a `<Link>`, and the two had already drifted apart -- neither
 * carried the `gap`, so neither could take an icon, which is why "New task"
 * (a real Button) has a plus on it and "New project" does not.
 *
 * `to` picks the router; `href` renders a plain anchor, which is what the
 * sign-in link needs -- an OAuth handshake is a top-level navigation and cannot
 * go through the router.
 */
type LinkButtonProps = {
  variant?: ButtonVariant;
  compact?: boolean;
  className?: string;
  children: ReactNode;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'className' | 'children'> &
  ({ to: string; href?: never } | { href: string; to?: never });

export function LinkButton({
  variant = 'secondary',
  compact = false,
  className = '',
  to,
  href,
  children,
  ...props
}: LinkButtonProps) {
  const classes = buttonClasses(variant, compact, className);

  if (to !== undefined) {
    return (
      <Link to={to} {...props} className={classes}>
        {children}
      </Link>
    );
  }

  return (
    <a href={href} {...props} className={classes}>
      {children}
    </a>
  );
}

/** Sized by the caller so it can sit inside a compact button without setting
 *  the row height. `currentColor`, so it inherits whatever it is placed in. */
export function Spinner({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      focusable="false"
      className={`shrink-0 animate-spin ${className}`}
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth={2.5} opacity={0.25} />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth={2.5}
        strokeLinecap="round"
      />
    </svg>
  );
}

const FIELD_BASE =
  `w-full rounded-lg border border-stone-300 bg-white text-stone-900 shadow-sm transition placeholder:text-stone-400 hover:border-stone-400 focus-visible:border-honey-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-honey-500/30 disabled:cursor-not-allowed disabled:bg-stone-50 disabled:text-stone-500`;

/**
 * Padding and type size are split out of the skin so a caller can pick the
 * shorter one without passing `className="py-1"`, which does not reliably win:
 * the caller's string is appended last but Tailwind breaks specificity ties by
 * its own emit order, not by source order. The same trap is spelled out in
 * ProjectTasks' filters.
 */
const FIELD_SIZING = {
  default: 'px-3 py-2 text-sm',
  /** 1.75rem tall -- the task panel's property-sheet rows, where a dozen of
   *  these stack up and every 6px is a row's worth of scrolling. */
  compact: 'px-2.5 py-1 text-dense',
} as const;

/** Named `compact` and not `size`: `size` is a real numeric HTML attribute on
 *  both input and select, and would be swallowed by the props spread. */
type FieldSizing = { compact?: boolean };

const fieldClasses = (compact: boolean | undefined, className: string) =>
  `${FIELD_BASE} ${FIELD_SIZING[compact ? 'compact' : 'default']} ${className}`;

/** `ComponentPropsWithRef` rather than `InputHTMLAttributes`, so a caller can
 *  hold the element and focus it -- the new-task panel puts the cursor back in
 *  the title after Create + new. React 19 passes `ref` through as an ordinary
 *  prop, so the spread below forwards it with no `forwardRef` wrapper. */
export function Input({
  className = '',
  compact,
  ...props
}: ComponentPropsWithRef<'input'> & FieldSizing) {
  return <input {...props} className={fieldClasses(compact, className)} />;
}

export function Textarea({
  className = '',
  compact,
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement> & FieldSizing) {
  return <textarea {...props} className={fieldClasses(compact, className)} />;
}

/**
 * Still a native `<select>`, deliberately: it gets the platform picker on a
 * phone and the platform keyboard behaviour everywhere, which a hand-rolled
 * listbox would have to earn back -- the reasoning BulkActionBar and TaskFields
 * already spell out.
 *
 * What changed is that it no longer *looks* native. `appearance-none` strips the
 * OS chrome and the chevron is drawn here, so a filter row reads as part of Hive
 * rather than as three pieces of the operating system sitting in the middle of
 * it. The right padding holds the text clear of that chevron.
 *
 * The wrapper is `inline-grid` with one cell: both children stack in the same
 * grid area, which places the chevron over the select without `relative` --
 * and so without introducing a stacking context, which would trap the popovers
 * that open next to these.
 */
export function Select({
  className = '',
  compact,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & FieldSizing) {
  return (
    <span className="grid w-full items-center [grid-template-areas:'field']">
      <select
        {...props}
        className={`${fieldClasses(compact, className)} appearance-none [grid-area:field] ${
          compact ? 'pr-7' : 'pr-8'
        }`}
      />
      <ChevronIcon
        className={`pointer-events-none justify-self-end text-stone-400 [grid-area:field] ${
          compact ? 'mr-1.5 h-3.5 w-3.5' : 'mr-2 h-4 w-4'
        }`}
      />
    </span>
  );
}

export function Field({
  label,
  htmlFor,
  hint,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  error?: string | null;
  children: ReactNode;
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-sm font-medium text-stone-700">
        {label}
      </label>
      <div className="mt-1.5">{children}</div>
      {error ? (
        <p className="mt-1 text-sm text-red-700">{error}</p>
      ) : hint ? (
        <p className="mt-1 text-sm text-stone-500">{hint}</p>
      ) : null}
    </div>
  );
}

/**
 * A message about something that just happened.
 *
 * It used to be error-only and a bare `<p>`, which had two consequences worth
 * naming. Callers that wanted a warning hand-rolled `bg-amber-50 text-amber-800`
 * themselves -- twice -- and the one caller that wanted a Dismiss button had to
 * nest a flex `<span>` inside the paragraph to lay it out, which meant this
 * could never grow real structure without breaking that page. Hence the `<div>`.
 *
 * The icon is not decoration: the fill colour is the only thing that separated
 * an error from a warning, and it is the one cue a colour-blind reader does not
 * get.
 *
 * `role="alert"` on the error tone only. It interrupts a screen reader, which is
 * right for "that did not save" and wrong for "saved".
 */
const ALERT_TONES = {
  error: {
    surface: 'border-red-200 bg-red-50 text-red-800',
    icon: 'text-red-600',
    Glyph: ErrorIcon,
  },
  // Amber, not honey. Honey is the brand accent -- it marks selection and the
  // current thing -- and a honey banner would read as one of those. Amber is
  // already what BLOCKED means everywhere else in the app.
  warning: {
    surface: 'border-amber-200 bg-amber-50 text-amber-900',
    icon: 'text-amber-600',
    Glyph: WarningIcon,
  },
  success: {
    surface: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    icon: 'text-emerald-600',
    Glyph: SuccessIcon,
  },
  info: {
    surface: 'border-stone-200 bg-stone-50 text-stone-700',
    icon: 'text-stone-500',
    Glyph: InfoIcon,
  },
} as const;

export type AlertTone = keyof typeof ALERT_TONES;

export function Alert({
  tone = 'error',
  title,
  icon,
  onDismiss,
  className = '',
  children,
}: {
  tone?: AlertTone;
  title?: string;
  /** Overrides the tone's glyph where a more specific one exists -- the task
   *  panel's prerequisite warning has a "blocked" icon that says more than a
   *  general warning triangle would. */
  icon?: ReactNode;
  /** Renders a close button. Omit for a message the page will clear itself. */
  onDismiss?: () => void;
  className?: string;
  children?: ReactNode;
}) {
  const { surface, icon: iconClass, Glyph } = ALERT_TONES[tone];

  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-2.5 rounded-lg border px-3 py-2.5 text-sm ${surface} ${className}`}
    >
      <span className={`mt-px flex h-4 w-4 shrink-0 items-center justify-center ${iconClass}`}>
        {icon ?? <Glyph className="h-4 w-4" />}
      </span>

      <div className="min-w-0 flex-1">
        {title ? <p className="font-medium">{title}</p> : null}
        {children ? <div className={title ? 'mt-0.5' : ''}>{children}</div> : null}
      </div>

      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className={`-m-1 shrink-0 rounded-md p-1 opacity-70 transition hover:bg-black/5 hover:opacity-100 ${FOCUS_RING_FLUSH}`}
        >
          <CloseIcon className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
  );
}

/**
 * The panel shell the dashboard columns share. Same treatment as ProjectCard's
 * outer surface, written once -- padding is left to the caller, because a card
 * whose body is a full-bleed list wants it on the rows, not the shell.
 */
export function Card({ className = '', children }: { className?: string; children: ReactNode }) {
  return (
    <section className={`rounded-xl border border-stone-200 bg-white shadow-sm ${className}`}>
      {children}
    </section>
  );
}

/**
 * The top of a page: back link, title, a count or badge beside it, a line of
 * description, and the page's primary action.
 *
 * Written once because there were five different versions of it -- Dashboard
 * put the signed-in email under the title, Projects had a title and a button and
 * no description, Assignees had all three at different spacings, and the two
 * editors had a bare heading with no way back. Which parts a page uses is now
 * the only thing that varies.
 *
 * `title` takes a node rather than a string so a page still loading its subject
 * can pass a skeleton bar. That is what stops the assignee editor rendering the
 * literal word "Assignee" for a beat and then flashing to the person's name.
 */
export function PageHeader({
  eyebrow,
  title,
  meta,
  description,
  actions,
  className = '',
}: {
  /** A back link or breadcrumb, above the title. */
  eyebrow?: ReactNode;
  title: ReactNode;
  /** A badge or count, on the title's baseline. */
  meta?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <header className={className}>
      {eyebrow ? <div className="mb-1.5">{eyebrow}</div> : null}

      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
          <h1 className="text-2xl font-semibold tracking-tight text-stone-900">{title}</h1>
          {meta}
        </div>

        {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
      </div>

      {description ? (
        <div className="mt-2 max-w-2xl text-sm text-stone-500">{description}</div>
      ) : null}
    </header>
  );
}

/**
 * A back link, shaped for `PageHeader`'s `eyebrow`.
 *
 * Only ProjectDetail had one of these. The two editors were reachable pages with
 * no way out of them but the browser's own back button, which after a save goes
 * back to the form.
 */
export function BackLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className={`-mx-1 inline-flex items-center gap-1 rounded-md px-1 py-0.5 text-sm text-stone-500 transition hover:text-stone-800 ${FOCUS_RING}`}
    >
      <ChevronLeftIcon className="h-3.5 w-3.5" />
      {children}
    </Link>
  );
}

/**
 * The segmented control, as class strings rather than a component.
 *
 * There were three of these: the project tab bar (a pill row of `NavLink`s),
 * the Projects card/list toggle (bordered, `<button>`s), and the dashboard's
 * due window (an inset track, `<button>`s). The third was the best of them --
 * a raised white segment on a recessed track reads as "this one is on" from
 * across the room, where the other two only tinted the active item one step
 * away from the hover colour next to it.
 *
 * Exported as strings so each caller keeps the element it needs. Wrapping them
 * would mean inventing a polymorphic `as` prop to hand `NavLink` back to the one
 * that is genuinely a set of links.
 */
export const SEGMENTED_TRACK =
  'inline-flex max-w-full items-center gap-0.5 overflow-x-auto rounded-lg bg-stone-100 p-0.5';

export const segmentedItem = (active: boolean) =>
  `whitespace-nowrap rounded-[0.3125rem] px-3 py-1 text-sm font-medium transition ${FOCUS_RING} ${
    active
      ? 'bg-white text-stone-900 shadow-sm'
      : 'text-stone-500 hover:text-stone-900'
  }`;

/**
 * Stand-in rows for a section whose contents are still in flight.
 *
 * Sized by a count the caller already has rather than by a guess: a task's list
 * row carries `subtaskCount`, `prerequisiteCount` and `attachmentCount`, so the
 * section can occupy its final height from the first frame and nothing below it
 * moves when the real rows land. A spinner of unknown size cannot do that, and
 * the shift it causes is most of what makes a panel feel slow.
 *
 * Bar widths cycle rather than being random: a re-render must not reshuffle
 * them, and three lengths are enough to stop the block reading as a table.
 */
const SKELETON_WIDTHS = ['w-1/2', 'w-2/3', 'w-5/12'];

export function SkeletonRows({
  count,
  /** Matched to the real row's height by the caller -- a bordered attachment
   *  row is taller than a plain subtask line. */
  rowClassName = 'h-5',
}: {
  count: number;
  rowClassName?: string;
}) {
  if (count <= 0) return null;

  return (
    // Hidden from the accessibility tree: it announces nothing a screen reader
    // can use, and the real rows replace it within a round trip.
    <ul className="mt-1.5 space-y-0.5" aria-hidden="true">
      {Array.from({ length: count }, (_, index) => (
        <li key={index} className={`flex items-center ${rowClassName}`}>
          <span
            className={`h-2.5 animate-pulse rounded bg-stone-200 ${
              SKELETON_WIDTHS[index % SKELETON_WIDTHS.length] ?? 'w-1/2'
            }`}
          />
        </li>
      ))}
    </ul>
  );
}

/**
 * The page-level counterpart to SkeletonRows.
 *
 * Every list in the app used to render the string "Loading…" -- the same line in
 * nine files -- so the content area collapsed to a single row of text and then
 * jumped to its full height when the data landed. That jump is most of what made
 * the app feel unfinished; it is also the one loading behaviour the panel had
 * already solved, two directories away.
 *
 * `bordered` matches the surface the real list will have, so the box itself does
 * not appear from nowhere either.
 */
export function SkeletonList({
  rows = 6,
  bordered = true,
  className = '',
}: {
  rows?: number;
  bordered?: boolean;
  className?: string;
}) {
  return (
    <div
      aria-hidden="true"
      className={`${
        bordered ? 'divide-y divide-stone-100 overflow-hidden rounded-xl border border-stone-200 bg-white' : 'space-y-px'
      } ${className}`}
    >
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="flex items-center gap-3 px-4 py-3">
          <span className="h-7 w-7 shrink-0 animate-pulse rounded-full bg-stone-200" />
          <span
            className={`h-3 animate-pulse rounded bg-stone-200 ${
              SKELETON_WIDTHS[index % SKELETON_WIDTHS.length] ?? 'w-1/2'
            }`}
          />
          <span className="ml-auto h-3 w-16 shrink-0 animate-pulse rounded bg-stone-100" />
        </div>
      ))}
    </div>
  );
}

/** Same job for the Projects card grid, whose rows are 16:9 tiles rather than
 *  lines -- a list skeleton under a grid would resettle into a completely
 *  different shape, which is the shift this is here to avoid. */
export function SkeletonCards({ count = 6 }: { count?: number }) {
  return (
    <div aria-hidden="true" className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="overflow-hidden rounded-xl border border-stone-200 bg-white">
          <div className="aspect-[16/9] w-full animate-pulse bg-stone-100" />
          <div className="space-y-2 p-4">
            <span className="block h-3.5 w-1/2 animate-pulse rounded bg-stone-200" />
            <span className="block h-3 w-4/5 animate-pulse rounded bg-stone-100" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Nothing here, and what to do about it.
 *
 * `action` takes a real button. Every empty state in the app used to end in an
 * underlined text link -- "Add the first person", "Create the first one" -- which
 * is the one place a call to action should not look like body copy.
 *
 * `compact` exists because the same box is used as a whole page and as the
 * contents of a one-third dashboard column, and `p-10` in the narrow one left
 * the title wrapping inside a mostly empty frame.
 */
export function EmptyState({
  icon,
  title,
  action,
  compact = false,
  children,
}: {
  icon?: ReactNode;
  title: string;
  action?: ReactNode;
  compact?: boolean;
  children?: ReactNode;
}) {
  return (
    <div
      className={`rounded-xl border border-dashed border-stone-300 bg-white text-center ${
        compact ? 'px-4 py-6' : 'px-6 py-10'
      }`}
    >
      {icon ? (
        <div className="mx-auto mb-2.5 flex h-9 w-9 items-center justify-center rounded-full bg-stone-100 text-stone-400">
          {icon}
        </div>
      ) : null}

      <p className="font-medium text-stone-700">{title}</p>
      {children ? <div className="mt-1 text-sm text-stone-500">{children}</div> : null}
      {action ? <div className="mt-4 flex justify-center">{action}</div> : null}
    </div>
  );
}

/**
 * The pill shell only -- the caller passes the fill and text colour, because
 * what a colour means differs per badge (project stage, task state).
 */
export function Badge({ className = '', children }: { className?: string; children: ReactNode }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${className}`}
    >
      {children}
    </span>
  );
}

/** A count beside a page or card title. Muted and lighter than the heading, so
 *  it reads as an annotation on the title rather than as part of it. */
export function CountBadge({ children }: { children: ReactNode }) {
  return (
    <span className="rounded-full bg-stone-100 px-2 py-0.5 text-xs font-medium text-stone-500">
      {children}
    </span>
  );
}
